import React, { useState } from 'react';
import { resolveZone, dateLabel } from '../dateUtils';
import { useProgress, progressTotals, groupByMonth, saveProgressTitle, deleteProgressEntry, TITLE_MAX } from '../progress';
import MoreMenu from './MoreMenu';
import { Icon } from './ui';
import { toast, confirmSheet } from './feedback';

// Study progress (brief 026): the user's finished sessions, private to them.
function Progress({ user, userProfile, go }) {
  const zone = resolveZone(userProfile?.timezone);
  const entries = useProgress(user.uid);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  if (!entries) return <div className="card"><div className="skeleton-row" /><div className="skeleton-row" /></div>;

  if (!entries.length) return (
    <div className="card empty-state">
      <div className="empty-icon"><Icon name="progress" size={40} /></div>
      <h3>No sessions yet</h3>
      <p className="small">Your completed sessions will appear here.</p>
      <button className="btn-small secondary" style={{ marginTop: '16px' }} onClick={() => go('matches')}>Find a partner</button>
    </div>
  );

  const totals = progressTotals(entries);
  const startEdit = e => { setEditing(e.id); setDraft(e.title); };
  const save = async e => {
    setSaving(true);
    try { await saveProgressTitle(user.uid, e.id, draft); setEditing(null); }
    catch (err) { console.error(err); toast('Could not save the title. Please try again.', { type: 'error' }); }
    finally { setSaving(false); }
  };
  const remove = async e => {
    const ok = await confirmSheet({ title: 'Delete this entry?', message: `"${e.title}" leaves your progress. ${e.partner} isn't told, and the session stays in their history.`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try { await deleteProgressEntry(user.uid, e.id); toast('Entry deleted.'); }
    catch (err) { console.error(err); toast('Could not delete the entry. Please try again.', { type: 'error' }); }
  };

  return (
    <>
      <p className="page-sub">Only you can see this.</p>
      <div className="card">
        <div className="reputation-stats progress-stats">
          <div className="stat-card"><span className="stat-value">{totals.sessions}</span><span className="stat-label">{totals.sessions === 1 ? 'Session' : 'Sessions'}</span></div>
          <div className="stat-card"><span className="stat-value">{totals.hours} h</span><span className="stat-label">Studied</span></div>
        </div>
        {totals.topSubjects.length > 0 && (
          <div className="progress-top">
            <span className="section-label">Top subjects</span>
            <div>{totals.topSubjects.map(s => <span key={s} className="chip">{s}</span>)}</div>
          </div>
        )}
      </div>

      {groupByMonth(entries, zone).map(g => (
        <section key={g.month} className="progress-month">
          <h2 className="section-label progress-month-label">{g.month}</h2>
          <div className="card progress-list">
            {g.entries.map(e => (
              <div key={e.id} className="progress-row">
                <div className="today-row-main">
                  {editing === e.id ? (
                    <form className="progress-edit" onSubmit={ev => { ev.preventDefault(); save(e); }}>
                      <input aria-label="Entry title" value={draft} maxLength={TITLE_MAX} autoFocus onChange={ev => setDraft(ev.target.value)} onKeyDown={ev => ev.key === 'Escape' && setEditing(null)} />
                      <div className="progress-edit-actions">
                        <button type="submit" className="btn-small" disabled={saving}>Save</button>
                        <button type="button" className="btn-small secondary" onClick={() => setEditing(null)}>Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <button type="button" className="progress-title" onClick={() => startEdit(e)} aria-label={`Edit title: ${e.title}`}>{e.title}</button>
                  )}
                  <div className="today-row-sub">{e.partner} · {dateLabel(e.start, zone)}</div>
                  {e.subjects.length > 0 && <div className="progress-chips">{e.subjects.map(s => <span key={s} className="chip">{s}</span>)}</div>}
                </div>
                <MoreMenu label={`More options for ${e.title}`} items={[
                  { label: 'Edit title', icon: 'check', onClick: () => startEdit(e) },
                  { label: 'Delete', icon: 'trash', danger: true, onClick: () => remove(e) },
                ]} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
export default Progress;
